import {
  Component,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  Output,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { ReCaptchaV3Service } from 'ng-recaptcha';
import { LanguageService } from 'src/app/shared/services/language.service';
import { LoginService } from '../../services/login.service';

@Component({
  selector: 'app-magic-link-request',
  templateUrl: './magic-link-request.component.html',
  styleUrls: ['./magic-link-request.component.scss'],
  standalone: true,
  imports: [TranslateModule, FormsModule],
})
export class MagicLinkRequestComponent implements OnDestroy {
  @Input() redirectPath: string | undefined;
  @Input() prefillEmail: string | undefined;
  @Output() changePage = new EventEmitter<{
    to?: 'login' | 'register' | 'reset' | 'magicRequest';
    email?: string;
  }>();

  email = '';
  isEmailEmpty = false;
  sent = false;
  sending = false;
  cooldownSeconds = 0;
  private intervalId: ReturnType<typeof setInterval> | null = null;

  private recaptchaV3Service = inject(ReCaptchaV3Service);
  private languageService = inject(LanguageService);
  private loginService = inject(LoginService);
  private router = inject(Router);

  constructor() {
    // initialize prefill and always compute current redirect from router URL
    Promise.resolve().then(() => {
      if (this.prefillEmail && !this.email) {
        this.email = (this.prefillEmail || '').trim().toLowerCase();
      }
      const current = this.computeCurrentRedirectPath();
      if (current) {
        this.redirectPath = current;
      }
    });
  }

  ngOnDestroy(): void {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
  }

  backToLogin() {
    this.changePage.emit({ to: 'login' });
  }

  send() {
    const email = this.email?.trim().toLowerCase();
    if (!email) {
      this.isEmailEmpty = true;
      return;
    }
    if (this.cooldownSeconds > 0 || this.sending) {
      return;
    }

    this.isEmailEmpty = false;
    this.sending = true;

    // Always refresh redirectPath from the current URL just before sending
    const current = this.computeCurrentRedirectPath();
    if (current) {
      this.redirectPath = current;
    }

    this.recaptchaV3Service
      .execute('magicLogin')
      .subscribe((_token: string) => {
        const languageCode = this.languageService.userSelectedLanguage
          .code as string;
        this.loginService
          .requestMagicLogin({
            email,
            languageCode,
            redirectPath: this.redirectPath,
          })
          .subscribe({
            next: () => {
              this.sent = true;
              this.sending = false;
              this.startCooldown(120);
            },
            error: () => {
              this.sending = false;
            },
          });
      });
  }

  onSubmit() {
    this.send();
  }

  private startCooldown(seconds: number) {
    this.cooldownSeconds = seconds;
    if (this.intervalId) {
      clearInterval(this.intervalId);
    }
    this.intervalId = setInterval(() => {
      this.cooldownSeconds -= 1;
      if (this.cooldownSeconds <= 0) {
        if (this.intervalId) {
          clearInterval(this.intervalId);
        }
        this.intervalId = null;
      }
    }, 1000);
  }

  private computeCurrentRedirectPath(): string {
    const currentUrl = this.router.url || '';
    if (!currentUrl) return '';

    // Use Router to parse current URL into a UrlTree for robust extraction
    const tree = this.router.parseUrl(currentUrl);
    const primary = tree.root.children['primary'];
    const segments = primary?.segments ?? [];
    let path = segments.map((s) => s.path).join('/');

    // Strip language prefix (e.g., it/, en/)
    const lang = (this.languageService.userSelectedLanguage?.code || '').toLowerCase();
    if (lang && (path === lang || path.startsWith(lang + '/'))) {
      path = path.substring(lang.length + (path.length > lang.length ? 1 : 0));
    }

    // Rebuild query string
    const qp = tree.queryParams || {} as Record<string, string | number | boolean>;
    const usp = new URLSearchParams();
    Object.keys(qp).forEach((k) => {
      const v = qp[k];
      if (v !== undefined && v !== null) {
        usp.set(k, String(v));
      }
    });
    const queryStr = usp.toString();
    const fragment = tree.fragment ? `#${tree.fragment}` : '';
    const rebuilt = queryStr ? `${path}?${queryStr}${fragment}` : `${path}${fragment}`;
    return rebuilt || 'home';
  }
}
